import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiTrash2, FiPhone, FiMail, FiCheck, FiCalendar, FiMapPin } from "react-icons/fi";
import api from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function MessagesList() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = () => {
    setLoading(true);
    api
      .get("/contact")
      .then((res) => setMessages(res.data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const markRead = async (id) => {
    try {
      const { data } = await api.put(`/contact/${id}/read`);
      setMessages(messages.map((m) => (m._id === id ? data : m)));
    } catch (err) {
      toast.error("Failed to update message");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success("Message deleted");
      setMessages(messages.filter((m) => m._id !== id));
    } catch (err) {
      toast.error("Failed to delete message");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <h1 className="font-heading text-2xl text-maroon-800 mb-6">Contact Messages</h1>

      {messages.length === 0 ? (
        <EmptyState title="No messages yet" subtitle="Messages submitted through the Contact page will appear here." />
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <div key={m._id} className={`bg-white rounded-2xl shadow-sm border p-5 ${m.read ? "border-gray-100" : "border-saffron-300"}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-heading text-maroon-800">{m.name}</p>
                    {!m.read && <span className="bg-saffron-500 text-white text-xs px-2 py-0.5 rounded-full">New</span>}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-2">
                    <span className="flex items-center gap-1"><FiPhone /> {m.phone}</span>
                    {m.email && <span className="flex items-center gap-1"><FiMail /> {m.email}</span>}
                    {m.service && <span className="bg-gold-50 text-maroon-700 px-2 py-0.5 rounded-full text-xs">{m.service}</span>}
                    {m.requestType && <span className="bg-saffron-50 text-saffron-700 px-2 py-0.5 rounded-full text-xs">{m.requestType}</span>}
                  </div>
                  {(m.preferredDate || m.location || m.birthDetails) && (
                    <div className="grid sm:grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                      {m.preferredDate && <span className="flex items-center gap-1"><FiCalendar /> {m.preferredDate}</span>}
                      {m.location && <span className="flex items-center gap-1"><FiMapPin /> {m.location}</span>}
                      {m.birthDetails && <span className="sm:col-span-2 bg-gray-50 rounded-lg px-3 py-2">Birth details: {m.birthDetails}</span>}
                    </div>
                  )}
                  <p className="text-gray-700 text-sm">{m.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{new Date(m.createdAt).toLocaleString("en-IN")}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!m.read && (
                    <button onClick={() => markRead(m._id)} title="Mark as read" className="p-2 rounded-lg hover:bg-gray-100 text-green-600">
                      <FiCheck />
                    </button>
                  )}
                  <button onClick={() => handleDelete(m._id)} title="Delete" className="p-2 rounded-lg hover:bg-gray-100 text-red-500">
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
